import { Box, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { FORTA_ERROR, FORTA_GRAY1 } from '../../utils/colors';

interface PDFViewerProps {
  fileUrl: string;
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const PDFViewer: React.FC<PDFViewerProps> = ({ fileUrl }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // Render each page at the container's width so the preview fits the window
  // and preserves the PDF's aspect ratio. A fixed scale rendered pages at a
  // size unrelated to the window, so larger PDFs overflowed the dialog.
  useEffect(() => {
    const container = containerRef.current;
    if (container == null) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        backgroundColor: FORTA_GRAY1,
      }}
    >
      <Document
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '47vw',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 500,
              }}
            >
              Loading document...
            </Typography>
          </Box>
        }
        error={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '47vw',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                color: FORTA_ERROR,
                fontSize: '18px',
                fontWeight: 500,
              }}
            >
              Error loading document
            </Typography>
          </Box>
        }
      >
        {containerWidth != null &&
          Array.from(new Array(numPages), (el, index) => (
            <Box
              key={`page_${index + 1}`}
              sx={{
                backgroundColor: FORTA_GRAY1,
                marginTop: '12px',
                marginBottom: '12px',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Page key={`page_${index + 1}`} pageNumber={index + 1} width={containerWidth} />
            </Box>
          ))}
      </Document>
    </Box>
  );
};

export default PDFViewer;
